/*
 * Recently viewed products, client side, for the modern storefront.
 *
 * Shopify has no server-side "recently viewed" API, so the handles live in
 * localStorage: every product page records its handle, and any page carrying the
 * recently-viewed section fetches each stored product's JSON and renders cards that
 * reuse the ms-card markup (and therefore the existing card styling).
 *
 * The section renders hidden and is revealed only once it has cards, so an empty
 * history never leaves a gap in the layout.
 */
(() => {
  const KEY = 'mstore:recently-viewed';
  const MAX_HISTORY = 12;
  const GRID = '[data-mstore-recently-viewed-grid]';
  const ROOT = '[data-mstore-recently-viewed]';

  const readHistory = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle) : [];
    } catch (error) {
      return [];
    }
  };

  const saveHistory = (handles) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(handles.slice(0, MAX_HISTORY)));
    } catch (error) {
      /* storage disabled (private mode, quota) - recently viewed simply stays empty */
    }
  };

  const record = () => {
    if (!document.body.classList.contains('template-product')) return;
    const el = document.querySelector('[data-mstore-product-handle]');
    const handle = el && el.getAttribute('data-mstore-product-handle');
    if (!handle) return;
    saveHistory([handle, ...readHistory().filter((stored) => stored !== handle)]);
  };

  const escapeHtml = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (char) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]
  ));

  const formatMoney = (cents, format) => {
    const value = Number(cents || 0) / 100;
    const fixed = value.toFixed(2);
    const parts = {
      amount: fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      amount_no_decimals: String(Math.round(value)),
      amount_with_comma_separator: fixed.replace('.', ','),
      amount_no_decimals_with_comma_separator: String(Math.round(value))
    };
    return String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => (key in parts ? parts[key] : match));
  };

  const imageUrl = (url, width) => `${url}${url.indexOf('?') > -1 ? '&' : '?'}width=${width}`;

  const cardHtml = (product, format) => {
    const variant = product.variants && product.variants[0];
    const preorder = Boolean(variant && variant.available && variant.inventory_management && variant.inventory_policy === 'continue' && Number(variant.inventory_quantity) <= 0);
    const soldOut = product.available === false;
    const compareAt = Number(product.compare_at_price_min || 0);
    const price = Number(product.price_min || 0);
    const onSale = compareAt > price;
    // Badge only deep discounts: with almost the whole catalogue on a compare-at price, "Sale"
    // on every card says nothing. 60% or deeper keeps the badge a real signal; the compare-at
    // price below still shows on any discount.
    const deepSale = onSale && Math.round((compareAt - price) / compareAt * 100) >= 60;

    let badge = '';
    if (preorder) badge = '<span class="ms-card__badge ms-card__badge--preorder">Pre-order</span>';
    else if (soldOut) badge = '<span class="ms-card__badge ms-card__badge--muted">Sold out</span>';
    else if (deepSale) badge = '<span class="ms-card__badge ms-card__badge--sale">Sale</span>';

    const image = product.featured_image
      ? `<img class="ms-card__image ms-card__image--primary" src="${imageUrl(product.featured_image, 600)}" srcset="${imageUrl(product.featured_image, 240)} 240w, ${imageUrl(product.featured_image, 360)} 360w, ${imageUrl(product.featured_image, 540)} 540w, ${imageUrl(product.featured_image, 720)} 720w" sizes="(min-width: 1000px) 25vw, 50vw" width="600" height="600" loading="lazy" alt="${escapeHtml(product.title)}">`
      : '';

    const compare = onSale
      ? `<span class="ms-card__compare">${formatMoney(product.compare_at_price_min, format)}</span>`
      : '';

    return `<a class="ms-card" href="/products/${encodeURIComponent(product.handle)}">
  <div class="ms-card__media">${image}${badge}<span class="ms-card__quick" aria-hidden="true">View details <span>&rarr;</span></span></div>
  <div class="ms-card__body">${product.type ? `<div class="ms-card__meta">${escapeHtml(product.type)}</div>` : ''}<div class="ms-card__title">${escapeHtml(product.title)}</div>
    <div class="ms-card__price"><strong>${product.price_varies ? 'From ' : ''}${formatMoney(product.price_min, format)}</strong>${compare}</div>
  </div>
</a>`;
  };

  const render = () => {
    const root = document.querySelector(ROOT);
    const grid = root && root.querySelector(GRID);
    if (!root || !grid) return;

    const exclude = root.getAttribute('data-exclude-handle') || '';
    const limit = Number(root.getAttribute('data-limit') || 8) || 8;
    const format = root.getAttribute('data-money-format') || '${{amount}}';
    const handles = readHistory().filter((handle) => handle !== exclude).slice(0, limit);
    if (!handles.length) return;

    Promise.all(handles.map((handle) => fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)))
      .then((products) => {
        const html = products.filter(Boolean).map((product) => cardHtml(product, format)).join('');
        if (!html) return;
        grid.innerHTML = html;
        root.hidden = false;
      })
      .catch(() => { /* leave the section hidden rather than show an empty grid */ });
  };

  record();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', render);
  else render();

  window.MStoreRecentlyViewed = { formatMoney, cardHtml, record, render };
})();
